import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PostCard = ({ post, currentUserId, onReaction, onComment }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);

  const postTypeIcons = {
    workout: 'Dumbbell',
    achievement: 'Award',
    milestone: 'Trophy',
    photo: 'Image',
    text: 'MessageSquare'
  };

  const postTypeColors = {
    workout: 'text-blue-500',
    achievement: 'text-yellow-500',
    milestone: 'text-purple-500',
    photo: 'text-green-500',
    text: 'text-gray-500'
  };

  const reactionEmojis = {
    like: '👍',
    love: '❤️',
    fire: '🔥',
    strong: '💪',
    clap: '👏'
  };

  const userReaction = post?.reactions?.find(r => r?.user_id === currentUserId);
  const displayComments = showAllComments ? post?.comments : post?.comments?.slice(0, 2);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const posted = new Date(timestamp);
    const diffMs = now - posted;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return posted?.toLocaleDateString();
  };

  const handleSubmitComment = () => {
    if (commentText?.trim()) {
      onComment(post?.id, commentText);
      setCommentText('');
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-4 md:p-6 hover-lift transition-smooth">
      {/* Post Header */}
      <div className="flex items-start gap-3 mb-4">
        <AppImage
          src={post?.user_profiles?.avatar_url}
          alt={post?.user_profiles?.full_name}
          className="w-12 h-12 rounded-full object-cover"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground">{post?.user_profiles?.full_name}</h3>
            <div className={`flex items-center gap-1 ${postTypeColors?.[post?.post_type]}`}>
              <Icon name={postTypeIcons?.[post?.post_type]} size={16} />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">{formatTimeAgo(post?.created_at)}</p>
        </div>
      </div>

      {/* Post Content */}
      <div className="mb-4">
        <p className="text-foreground whitespace-pre-wrap mb-3">{post?.content}</p>
        
        {/* Workout Data */}
        {post?.workout_data && (
          <div className="bg-accent/50 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 text-sm">
              <Icon name="Dumbbell" size={16} className="text-primary" />
              <span className="font-medium text-foreground">
                {post?.workout_data?.exercise || post?.workout_data?.type}
              </span>
              {post?.workout_data?.weight && (
                <span className="text-muted-foreground">
                  • {post?.workout_data?.weight} {post?.workout_data?.unit} × {post?.workout_data?.reps} reps
                </span>
              )}
              {post?.workout_data?.duration && (
                <span className="text-muted-foreground">
                  • {post?.workout_data?.duration} min • {post?.workout_data?.calories} cal
                </span>
              )}
            </div>
          </div>
        )}

        {/* Media */}
        {post?.media_url && (
          <AppImage
            src={post?.media_url}
            alt="Post media"
            className="w-full rounded-lg object-cover max-h-96"
          />
        )}
      </div>

      {/* Post Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3 pb-3 border-b border-border">
        <span>{post?.likes_count || 0} reactions</span>
        <span>{post?.comments_count || 0} comments</span>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onReaction(post?.id, 'like')}
          className={`flex-1 ${userReaction ? 'text-primary' : ''}`}
        >
          <Icon name="ThumbsUp" size={18} className="mr-2" />
          {userReaction ? reactionEmojis?.[userReaction?.reaction_type] : 'Like'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowComments(!showComments)}
          className="flex-1"
        >
          <Icon name="MessageCircle" size={18} className="mr-2" />
          Comment
        </Button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-border pt-4">
          {/* Existing Comments */}
          {post?.comments?.length > 0 && (
            <div className="space-y-3 mb-4">
              {displayComments?.map((comment) => (
                <div key={comment?.id} className="flex gap-2">
                  <AppImage
                    src={comment?.user_profiles?.avatar_url}
                    alt={comment?.user_profiles?.full_name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex-1 bg-accent rounded-lg p-3">
                    <p className="font-semibold text-sm text-foreground mb-1">
                      {comment?.user_profiles?.full_name}
                    </p>
                    <p className="text-sm text-foreground">{comment?.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatTimeAgo(comment?.created_at)}
                    </p>
                  </div>
                </div>
              ))}
              {post?.comments?.length > 2 && !showAllComments && (
                <button
                  onClick={() => setShowAllComments(true)}
                  className="text-sm text-primary hover:underline"
                >
                  View all {post?.comments?.length} comments
                </button>
              )}
            </div>
          )}

          {/* Add Comment */}
          <div className="flex gap-2">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleSubmitComment()}
              placeholder="Write a comment..."
              className="flex-1 px-4 py-2 rounded-lg bg-accent border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
            <Button
              onClick={handleSubmitComment}
              disabled={!commentText?.trim()}
              size="sm"
            >
              <Icon name="Send" size={18} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostCard;